
import { countryMapping } from './CountryMapping';
import { regionMapping } from './RegionMapping';
import { cityMapping } from './CityMapping';

export const locationMapping = {
  countries: countryMapping,
  regions: regionMapping,
  cities: cityMapping
};

export interface DetectedLocation {
  type: 'country' | 'region' | 'city';
  info: any;
  priority: number; // Higher number = higher priority
}

export interface DetectedMultipleLocations {
  type: 'multiple_cities' | 'multiple_regions' | 'multiple_countries';
  locations: DetectedLocation[];
  priority: number;
}

export const detectLocation = (searchTerms: string[]): DetectedLocation | DetectedMultipleLocations | null => {
  console.log('=== STRICT LOCATION DETECTION ===');
  console.log('Original search terms:', searchTerms);
  
  // Check for "of" queries with multiple locations first
  const fullQuery = searchTerms.join(' ').toLowerCase();
  const multipleLocationResult = detectMultipleLocations(fullQuery);
  if (multipleLocationResult) {
    console.log('🎯 DETECTED MULTIPLE LOCATIONS:', multipleLocationResult);
    return multipleLocationResult;
  }
  
  // Enhanced cleaning for "nearby" phrases
  const cleanedTerms = cleanSearchTermsForLocation(searchTerms);
  console.log('Cleaned search terms for location detection:', cleanedTerms);
  
  let detectedLocations: DetectedLocation[] = [];
  
  // SPECIAL HANDLING FOR COSTA BRAVA - Always treat as region, never as city
  const searchString = searchTerms.join(' ').toLowerCase();
  if (searchString.includes('costa brava')) {
    console.log('🏖️ SPECIAL COSTA BRAVA DETECTION - Always treat as REGION');
    detectedLocations.push({ 
      type: 'region', 
      info: { region: 'Costa Brava', country: 'Spanje' }, 
      priority: 10 // Highest priority
    });
    console.log('🎯 DETECTED COSTA BRAVA AS REGION with highest priority');
    return detectedLocations[0];
  }
  
  // Check for cities first (highest priority) - but exclude Costa Brava
  console.log('=== CHECKING CITIES (HIGHEST PRIORITY) ===');
  for (const [term, info] of Object.entries(locationMapping.cities)) {
    // Skip Costa Brava in city detection
    if (term.toLowerCase().includes('costa brava')) {
      console.log('⏭️ Skipping Costa Brava in city detection - handled separately');
      continue;
    }
    
    const termLower = term.toLowerCase();
    const matched = cleanedTerms.some(searchTerm => {
      const searchLower = searchTerm.toLowerCase();
      
      // Exact match gets highest priority
      if (searchLower === termLower) {
        console.log(`✅ EXACT CITY MATCH: "${searchTerm}" === "${term}"`);
        return true;
      }
      
      // Partial matches for cities
      if (searchLower.includes(termLower) || termLower.includes(searchLower)) {
        console.log(`✅ PARTIAL CITY MATCH: "${searchTerm}" ~ "${term}"`);
        return true;
      }
      
      return false;
    });
    
    if (matched) {
      console.log('🎯 DETECTED CITY:', term, '→', info);
      detectedLocations.push({ type: 'city', info, priority: 3 });
    }
  }
  
  // Check for regions (medium priority)
  console.log('=== CHECKING REGIONS (MEDIUM PRIORITY) ===');
  for (const [term, info] of Object.entries(locationMapping.regions)) {
    const termLower = term.toLowerCase();
    const matched = cleanedTerms.some(searchTerm => {
      const searchLower = searchTerm.toLowerCase();
      
      if (searchLower === termLower) return true;
      if (termLower.includes('garda') && searchLower.includes('garda')) return true;
      if (termLower.includes('gardameer') && searchLower.includes('gardameer')) return true;
      if (searchLower.includes(termLower) || termLower.includes(searchLower)) return true;
      
      return false;
    });
    
    if (matched) {
      console.log('🎯 DETECTED REGION:', term, '→', info);
      detectedLocations.push({ type: 'region', info, priority: 2 });
    }
  }
  
  // Check for countries (lowest priority)
  console.log('=== CHECKING COUNTRIES (LOWEST PRIORITY) ===');
  for (const [term, country] of Object.entries(locationMapping.countries)) {
    const termLower = term.toLowerCase();
    console.log(`🔍 Checking country mapping: "${term}" (${termLower}) → "${country}"`);
    
    const matched = cleanedTerms.some(searchTerm => {
      const searchLower = searchTerm.toLowerCase();
      
      console.log(`  - Comparing search term "${searchLower}" with country term "${termLower}"`);
      
      if (searchLower === termLower) {
        console.log('    ✅ EXACT COUNTRY MATCH!');
        return true;
      }
      
      if (searchLower.includes(termLower)) {
        console.log('    ✅ SEARCH CONTAINS COUNTRY TERM!');
        return true;
      }
      
      if (termLower.includes(searchLower)) {
        console.log('    ✅ COUNTRY TERM CONTAINS SEARCH!');
        return true;
      }
      
      console.log('    ❌ No match');
      return false;
    });
    
    if (matched) {
      console.log(`🎯 DETECTED COUNTRY: "${term}" → "${country}"`);
      detectedLocations.push({ type: 'country', info: { country }, priority: 1 });
    }
  }
  
  // ENHANCED LOGIC: Prioritize the most specific location found
  if (detectedLocations.length > 0) {
    detectedLocations.sort((a, b) => b.priority - a.priority);
    
    // For exact matches, prioritize even higher
    const exactMatches = detectedLocations.filter(loc => {
      if (loc.type === 'city') {
        const cityName = Object.keys(locationMapping.cities).find(key => locationMapping.cities[key] === loc.info);
        return cleanedTerms.some(term => term.toLowerCase() === cityName?.toLowerCase());
      }
      return false;
    });
    
    if (exactMatches.length > 0) {
      console.log('🏆 FOUND EXACT CITY MATCH - HIGHEST PRIORITY');
      console.log('Selected exact match:', exactMatches[0]);
      return exactMatches[0];
    }
    
    const selectedLocation = detectedLocations[0];
    console.log('🏆 SELECTED LOCATION (HIGHEST PRIORITY):', selectedLocation);
    console.log('📍 Other detected locations (ignored):', detectedLocations.slice(1));
    
    return selectedLocation;
  }
  
  console.log('❌ NO LOCATION DETECTED from terms:', cleanedTerms);
  return null;
};

// Function to detect multiple locations in "of" queries
const detectMultipleLocations = (query: string): DetectedMultipleLocations | null => {
  console.log('🔍 Checking for multiple locations in query:', query);
  
  // Look for "of" pattern
  if (query.includes(' of ')) {
    const parts = query.split(' of ').map(part => part.trim());
    console.log('Found "of" pattern, parts:', parts);
    
    const detectedLocations = [];
    
    for (const part of parts) {
      // Check if this part is a city
      for (const [city, info] of Object.entries(locationMapping.cities)) {
        if (part.toLowerCase().includes(city.toLowerCase()) || city.toLowerCase().includes(part.toLowerCase())) {
          console.log(`✅ Found city in part "${part}": ${city}`);
          detectedLocations.push({
            type: 'city' as const,
            info,
            priority: 3
          });
          break;
        }
      }
    }
    
    if (detectedLocations.length >= 2) {
      console.log('🎯 Multiple cities detected:', detectedLocations);
      return {
        type: 'multiple_cities',
        locations: detectedLocations,
        priority: 10 // Highest priority for explicit multi-location queries
      };
    }
  }
  
  return null;
};

// Enhanced function to clean search terms for better location detection
const cleanSearchTermsForLocation = (searchTerms: string[]): string[] => {
  const searchString = searchTerms.join(' ').toLowerCase();
  
  // Handle "nearby" patterns more comprehensively
  const nearbyPatterns = [
    /in de buurt van (.+)/,
    /bij (.+)/,
    /rond (.+)/,
    /rondom (.+)/,
    /nabij (.+)/,
    /near (.+)/,
    /around (.+)/,
    /close to (.+)/,
    /dichtbij (.+)/,
    /vlakbij (.+)/
  ];
  
  for (const pattern of nearbyPatterns) {
    const match = searchString.match(pattern);
    if (match && match[1]) {
      console.log(`🎯 Detected "nearby" pattern: "${match[0]}" → extracting location: "${match[1]}"`);
      // Return the location part as separate terms, cleaned of common words
      const locationPart = match[1].trim();
      const locationTerms = locationPart.split(' ').filter(term => term.length > 1);
      console.log('🧹 Cleaned location terms:', locationTerms);
      return locationTerms;
    }
  }
  
  // Enhanced filler words cleaning
  const fillerWords = [
    'een', 'de', 'het', 'in', 'op', 'bij', 'naar', 'voor', 'met', 'aan', 'van', 'te', 'is', 'zijn',
    'waar', 'ik', 'wil', 'zou', 'kan', 'moet', 'heel', 'erg', 'zeer', 'ook', 'nog', 'wel', 'niet',
    'maar', 'en', 'of', 'dus', 'want', 'omdat', 'dat', 'die', 'dit', 'deze', 'daar', 'hier'
  ];
  
  return searchTerms.filter(term => {
    const lowerTerm = term.toLowerCase();
    return !fillerWords.includes(lowerTerm) && lowerTerm.length > 1;
  });
};
